<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Check, Palette, Plus, Settings2 } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getTenantTaskPriorities, getTenantTaskStatuses } from '~/lib/taskConfig';
import type { TenantTaskPriorityOption, TenantTaskStatusOption } from '~/types/models';
import { toast } from 'vue-sonner';

type TaskOption = TenantTaskStatusOption | TenantTaskPriorityOption;

const props = withDefaults(defineProps<{
    modelValue?: string;
    type: 'status' | 'priority';
    options?: TaskOption[];
    placeholder?: string;
    triggerClass?: string;
    disabled?: boolean;
    allowClear?: boolean;
    clearLabel?: string;
    createLabel?: string;
}>(), {
    modelValue: '',
    options: () => [],
    placeholder: 'Select option',
    triggerClass: '',
    disabled: false,
    allowClear: false,
    clearLabel: 'Clear',
    createLabel: 'Create new',
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
}>();

const tenantStore = useTenantStore();
const open = ref(false);
const managing = ref(false);
const saving = ref(false);
const searchQuery = ref('');
const draftColor = ref('#64748b');
const editableOptions = ref<TaskOption[]>([]);
const recentValues = ref<string[]>([]);

const selectedOption = computed(() => (
    props.options.find(option => option.value === props.modelValue) || null
));

const filteredOptions = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return props.options;
    return props.options.filter(option =>
        option.label.toLowerCase().includes(query) || option.value.toLowerCase().includes(query),
    );
});

const recentStorageKey = computed(() => {
    const tenantId = String(tenantStore.currentTenantId || '').trim();
    return tenantId ? `task-option-menu:${tenantId}:${props.type}` : '';
});

function loadRecents() {
    if (!process.client || !recentStorageKey.value) {
        recentValues.value = [];
        return;
    }

    try {
        const stored = window.localStorage.getItem(recentStorageKey.value);
        const parsed = stored ? JSON.parse(stored) : [];
        recentValues.value = Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
        recentValues.value = [];
    }
}

function persistRecent(value: string) {
    if (!process.client || !recentStorageKey.value || !value) return;
    const next = [value, ...recentValues.value.filter(entry => entry !== value)].slice(0, 10);
    recentValues.value = next;
    window.localStorage.setItem(recentStorageKey.value, JSON.stringify(next));
}

function sortByRecency(options: TaskOption[]) {
    const order = new Map(recentValues.value.map((value, index) => [value, index] as const));
    return [...options].sort((a, b) => {
        const aIndex = order.get(a.value);
        const bIndex = order.get(b.value);
        const aHas = typeof aIndex === 'number';
        const bHas = typeof bIndex === 'number';
        if (aHas && bHas) return aIndex - bIndex;
        if (aHas) return -1;
        if (bHas) return 1;
        return a.label.localeCompare(b.label);
    });
}

const visibleOptions = computed(() => {
    const source = searchQuery.value.trim() ? filteredOptions.value : props.options;
    return sortByRecency(source).slice(0, 10);
});

const canCreate = computed(() => {
    const label = searchQuery.value.trim();
    if (!label) return false;

    const normalizedValue = props.type === 'priority' ? slugify(label) : label;
    return !props.options.some(option =>
        option.label.trim().toLowerCase() === label.toLowerCase()
        || option.value.trim().toLowerCase() === normalizedValue.toLowerCase(),
    );
});

watch(
    () => props.options,
    (options) => {
        editableOptions.value = options.map(option => ({ ...option }));
    },
    { immediate: true, deep: true },
);

watch(open, (isOpen) => {
    if (isOpen) {
        managing.value = false;
        searchQuery.value = '';
        draftColor.value = selectedOption.value?.color || (props.type === 'status' ? '#64748b' : '#475569');
        editableOptions.value = props.options.map(option => ({ ...option }));
        loadRecents();
    }
});

function slugify(label: string) {
    return label
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

function closeMenu() {
    open.value = false;
}

function select(value: string) {
    emit('update:modelValue', value);
    persistRecent(value);
    closeMenu();
}

function clearSelection() {
    emit('update:modelValue', '');
    closeMenu();
}

function currentSettingsPayload(nextOptions: TaskOption[]) {
    if (props.type === 'status') {
        return {
            statuses: nextOptions as TenantTaskStatusOption[],
            priorities: getTenantTaskPriorities(tenantStore.currentTenant),
        };
    }

    return {
        statuses: getTenantTaskStatuses(tenantStore.currentTenant),
        priorities: nextOptions as TenantTaskPriorityOption[],
    };
}

async function saveNewOption() {
    const label = searchQuery.value.trim();
    if (!label || !tenantStore.currentTenantId || saving.value) return;

    const value = props.type === 'priority' ? slugify(label) : label;
    if (!value) return;

    saving.value = true;
    try {
        const nextOptions = [
            ...props.options,
            {
                value,
                label,
                color: draftColor.value || (props.type === 'status' ? '#64748b' : '#475569'),
            },
        ];

        await tenantStore.setTaskSettings(tenantStore.currentTenantId, currentSettingsPayload(nextOptions));
        emit('update:modelValue', value);
        closeMenu();
    } catch (error: any) {
        toast.error(error?.message || `Failed to create ${props.type}.`);
    } finally {
        saving.value = false;
    }
}

async function saveManagedOptions() {
    if (!tenantStore.currentTenantId || saving.value) return;

    saving.value = true;
    try {
        const nextOptions = editableOptions.value.map(option => ({
            ...option,
            color: String(option.color || '').trim() || (props.type === 'status' ? '#64748b' : '#475569'),
        }));

        await tenantStore.setTaskSettings(tenantStore.currentTenantId, currentSettingsPayload(nextOptions));
        managing.value = false;
        toast.success(`${props.type === 'status' ? 'Statuses' : 'Priorities'} updated.`);
    } catch (error: any) {
        toast.error(error?.message || `Failed to update ${props.type} colours.`);
    } finally {
        saving.value = false;
    }
}
</script>

<template>
    <Popover v-model:open="open">
        <PopoverTrigger as-child>
            <button
                type="button"
                :disabled="disabled"
                :class="['w-full', triggerClass]"
            >
                <slot name="trigger" :option="selectedOption">
                    <span class="text-sm text-foreground">
                        {{ selectedOption?.label || placeholder }}
                    </span>
                </slot>
            </button>
        </PopoverTrigger>

        <PopoverContent class="w-80 rounded-2xl border-border/70 p-3" align="start" @open-auto-focus.prevent>
            <div v-if="!managing" class="space-y-3">
                <div class="flex items-start justify-between gap-3">
                    <div>
                        <p class="text-sm font-medium">{{ type === 'status' ? 'Status' : 'Priority' }}</p>
                        <p class="text-xs text-muted-foreground">
                            Choose an option or create one without leaving the task.
                        </p>
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        class="h-8 px-2"
                        @click="managing = true"
                    >
                        <Settings2 class="mr-1 h-4 w-4" />
                        Manage
                    </Button>
                </div>

                <Input
                    v-model="searchQuery"
                    class="h-10 rounded-xl"
                    :placeholder="`Search or create ${type}...`"
                />

                <div class="max-h-64 space-y-1 overflow-y-auto">
                    <button
                        v-if="allowClear"
                        type="button"
                        class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-muted/60"
                        @click="clearSelection"
                    >
                        <span class="text-sm text-muted-foreground">{{ clearLabel }}</span>
                        <Check v-if="!modelValue" class="h-4 w-4 text-primary" />
                    </button>

                    <button
                        v-for="option in visibleOptions"
                        :key="option.value"
                        type="button"
                        class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-muted/60"
                        @click="select(option.value)"
                    >
                        <div class="flex items-center gap-3">
                            <span class="h-2.5 w-2.5 rounded-full" :style="{ backgroundColor: option.color || '#64748b' }" />
                            <span class="text-sm font-medium text-foreground">{{ option.label }}</span>
                        </div>
                        <Check v-if="modelValue === option.value" class="h-4 w-4 text-primary" />
                    </button>
                </div>

                <div v-if="canCreate" class="space-y-2 border-t border-border/70 pt-3">
                    <p class="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">{{ createLabel }}</p>
                    <div class="flex items-center gap-2">
                        <Input v-model="draftColor" type="color" class="h-10 w-14 rounded-xl p-1" />
                        <Button
                            type="button"
                            variant="outline"
                            class="flex-1 justify-start rounded-xl"
                            :disabled="saving"
                            @click="saveNewOption"
                        >
                            <Plus class="mr-2 h-4 w-4" />
                            Create "{{ searchQuery.trim() }}"
                        </Button>
                    </div>
                </div>
            </div>

            <div v-else class="space-y-3">
                <div class="flex items-start justify-between gap-3">
                    <div>
                        <p class="text-sm font-medium">Manage {{ type === 'status' ? 'statuses' : 'priorities' }}</p>
                        <p class="text-xs text-muted-foreground">Update the colours used across the workspace.</p>
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        class="h-8 px-2"
                        @click="managing = false"
                    >
                        Back
                    </Button>
                </div>

                <div class="max-h-72 space-y-2 overflow-y-auto">
                    <div
                        v-for="option in editableOptions"
                        :key="option.value"
                        class="flex items-center gap-3 rounded-xl border border-border/70 px-3 py-2"
                    >
                        <Input v-model="option.color" type="color" class="h-10 w-14 rounded-xl p-1" />
                        <div class="min-w-0 flex-1">
                            <p class="truncate text-sm font-medium text-foreground">{{ option.label }}</p>
                            <p class="truncate text-xs text-muted-foreground">{{ option.value }}</p>
                        </div>
                        <Palette class="h-4 w-4 text-muted-foreground" />
                    </div>
                </div>

                <div class="flex justify-end">
                    <Button type="button" class="rounded-xl" :disabled="saving" @click="saveManagedOptions">
                        {{ saving ? 'Saving...' : 'Save colours' }}
                    </Button>
                </div>
            </div>
        </PopoverContent>
    </Popover>
</template>
